import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import AddIcon from "@atlaskit/icon/core/add";
import type {CalendarApi, DatesSetArg} from "@fullcalendar/core/index.js";
import type FullCalendar from "@fullcalendar/react";
import {token} from "@atlaskit/tokens";
import {useMediaQuery} from "@mantine/hooks";
import {useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, useTransition, type RefObject, type ReactNode} from "react";
import {DateTime} from "luxon";
import {CalendarDayEventsModal} from "~/components/beatPlanningCalendar/calendarDayEventsModal";
import {collapseTimedEventsToCountSummaries} from "~/components/beatPlanningCalendar/calendarEventCountDisplay";
import type {CalendarDaySummaryModalPresentation} from "~/components/beatPlanningCalendar/calendarDaySummaryModalPresentation";
import {CalendarMonthWeekDayView} from "~/components/beatPlanningCalendar/calendarMonthWeekDayView";
import {CalendarScheduleList, type CalendarScheduleGroup} from "~/components/beatPlanningCalendar/calendarScheduleList";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {useAnankePreferences} from "@vesta/ananke/contexts/anankeClientContext";
import {
    clearCalendarToolbarHandlers,
    getDefaultCalendarViewModeForViewport,
    MOBILE_CALENDAR_VIEWPORT_MEDIA_QUERY,
    readIsMobileCalendarViewportSync,
    resolveNonYearCalendarViewMode,
    setCalendarToolbarState,
    type CalendarViewMode,
} from "~/components/beatPlanningCalendar/calendarToolbarBus";
import {CalendarYearView} from "~/components/beatPlanningCalendar/calendarYearView";
import {useCalendarStats, type DerivedEventCalendarStats} from "~/components/beatPlanningCalendar/useCalendarStats";
import {CALENDAR_DAY_MODAL_PAGE_SIZE} from "~/components/ottopilotDataViewer/calendarFetchLimits";
import {useCalendarSwipeNavigation} from "~/components/beatPlanningCalendar/useCalendarSwipeNavigation";
import {useAsidePanelContextSafe} from "~/contexts/asidePanelContext";
import {createCalendarDayRequestGuard} from "~/components/beatPlanningCalendar/calendarDayRequestGuard";
import "./beatPlanningCalendar.css";

export type BeatPlanningCalendarEvent = {
    id: string;
    title: string;
    start: string | Date;
    end: string | Date;
    allDay?: boolean;
    /** Optional type used to derive event color when getEventColor is provided. */
    type?: string;
    /** Optional human-readable type label used in legend dropdown. */
    typeLabel?: string;
    /** Optional event template label used in schedule card chip. */
    templateLabel?: string;
    templateStrokeColor?: string;
    extendedProps?: {
        rawRow?: unknown;
        typeLabel?: string;
        templateLabel?: string;
        templateStrokeColor?: string;
        /** Synthetic `+N` tile in time-grid when multiple events share the same start minute. */
        isEventCountSummary?: boolean;
        summarySourceEvents?: Array<BeatPlanningCalendarEvent>;
        summaryCellEvents?: Array<BeatPlanningCalendarEvent>;
    };
};

export type EventColorMap = {
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
};

function toLocalDayKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function syncMultiMonthYearHeaderTotals(
    calendarRootEl: HTMLElement,
    calendarApi: CalendarApi,
    monthTotals: Map<string, number>,
    monthTypeBars: Map<string, string>,
): void {
    if (calendarApi.view.type !== "multiMonthYear") {
        return;
    }
    calendarRootEl.querySelectorAll(".fc-multimonth-month[data-date]").forEach((node: Element) => {
        const monthKey = node.getAttribute("data-date");
        if (monthKey === null) {
            return;
        }
        const header = node.querySelector(".fc-multimonth-header");
        if (header === null) {
            return;
        }
        const isoMonthKey = monthKey.slice(0, 7);
        const n = monthTotals.get(isoMonthKey) ?? monthTotals.get(monthKey) ?? 0;
        header.setAttribute("data-beat-month-total", n === 0 ? "" : `${n} ${n === 1 ? "event" : "events"}`);
        const bar = monthTypeBars.get(isoMonthKey) ?? monthTypeBars.get(monthKey);
        if (bar === undefined) {
            (header as HTMLElement).style.removeProperty("--beat-month-type-bar");
        } else {
            (header as HTMLElement).style.setProperty("--beat-month-type-bar", bar);
        }
    });
}

type CalendarSelectableEvent = {
    id: string;
    title: string;
    start: Date | null;
    end: Date | null;
    allDay: boolean;
    extendedProps: unknown;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
};

export function BeatPlanningCalendar<T>({
    events,
    onEventClick,
    getEventColor,
    getEventTypeLabel,
    hasActiveFilters = false,
    isScheduleDataLoading = false,
    scheduleHasMore = false,
    isScheduleLoadingMore = false,
    scheduleLoadMoreError = null,
    onScheduleLoadMore,
    onCreateEventClick,
    scheduleEmptyState,
    legendItems,
    allDayStyle = {mode: "fixedGreen"},
    startTimeOnly = false,
    use24HourTimeAxis = false,
    disableCreateEventWhenViewYearAfterCurrent = false,
    disableCreateEventButton = false,
    eventOpensDaySummaryModal = false,
    daySummaryModalPresentation,
    onVisibleRangeChange,
    onCalendarViewModeChange,
    externalYearStats,
    fetchDayEventsForModal,
    dayModalRequestContextKey = "default",
    className,
}: {
    events: Array<BeatPlanningCalendarEvent>;
    onEventClick: (event: BeatPlanningCalendarEvent, rawRow?: T) => void;
    /** Optional. Maps event type to backgroundColor/borderColor/textColor. When provided, events with a type will be colored accordingly. */
    getEventColor?: (type: string | undefined) => EventColorMap;
    /** Optional. Maps type key/uuid to readable label in legend. */
    getEventTypeLabel?: (type: string) => string;
    hasActiveFilters?: boolean;
    /** When true, suppress schedule-list empty state while data is loading. */
    isScheduleDataLoading?: boolean;
    scheduleHasMore?: boolean;
    isScheduleLoadingMore?: boolean;
    scheduleLoadMoreError?: string | null;
    onScheduleLoadMore?: () => void;
    onCreateEventClick?: () => void;
    /** Optional. Copy for the schedule list empty state; defaults to event-oriented strings. */
    scheduleEmptyState?: {
        createButtonLabel: string;
        title: string;
        titleWithFilters: string;
        description: string;
        descriptionWithFilters: string;
    };
    /** Optional. Explicit legend items; when provided legend does not depend on visible events. */
    legendItems?: Array<{type: string; label: string; description?: string}>;
    /** Optional. Controls whether all-day cards are fixed green or inherit status color. */
    allDayStyle?: {mode: "fixedGreen" | "inheritStatus"};
    /** Optional. When true, schedule list shows only start time instead of start-end range. */
    startTimeOnly?: boolean;
    /** When true, time-grid axis and brief event times use 24-hour format (HH:mm). */
    use24HourTimeAxis?: boolean;
    /**
     * When true, the schedule empty-state "Create event" button is disabled while the visible
     * calendar period starts in a year after the current local calendar year.
     */
    disableCreateEventWhenViewYearAfterCurrent?: boolean;
    /** When true, the schedule empty-state "Create event" button is always disabled (visual + interaction). */
    disableCreateEventButton?: boolean;
    /** Optional. Emits whenever visible date range changes in the active calendar view. */
    onVisibleRangeChange?: (range: {start: Date; end: Date}) => void;
    /** Optional. Emits whenever the active calendar mode changes. */
    onCalendarViewModeChange?: (mode: CalendarViewMode) => void;
    className?: string;
    /** When true, event clicks open the day summary modal for that event's date first. */
    eventOpensDaySummaryModal?: boolean;
    daySummaryModalPresentation?: CalendarDaySummaryModalPresentation<T>;
    /** When set (Year aggregate fetch), drives year badges without loading full event rows. */
    externalYearStats?: DerivedEventCalendarStats;
    /** When set with `eventOpensDaySummaryModal`, day modal loads on click instead of from cached month rows. */
    fetchDayEventsForModal?: (
        date: Date,
        offset: number,
    ) => Promise<Array<CalendarSelectableEvent>>;
    dayModalRequestContextKey?: string;
}) {
    const initialDefaultViewMode = getDefaultCalendarViewModeForViewport(readIsMobileCalendarViewportSync());
    const initialNonYearCalendarViewMode = resolveNonYearCalendarViewMode(initialDefaultViewMode);
    const isMobile = useMediaQuery(MOBILE_CALENDAR_VIEWPORT_MEDIA_QUERY, false, {getInitialValueInEffect: true});
    const asidePanelContext = useAsidePanelContextSafe();
    const yearCalendarRef = useRef<FullCalendar | null>(null);
    const nonYearCalendarRef = useRef<FullCalendar | null>(null);
    const calendarRootRef = useRef<HTMLDivElement | null>(null);
    const calendarContainerRef = useRef<HTMLDivElement | null>(null);
    const [isDayEventsModalOpen, setIsDayEventsModalOpen] = useState(false);
    const [selectedPeriodTitle, setSelectedPeriodTitle] = useState("");
    const [selectedPeriodEvents, setSelectedPeriodEvents] = useState<Array<CalendarSelectableEvent>>([]);
    const [selectedDayDate, setSelectedDayDate] = useState<Date | null>(null);
    const [dayModalHasMore, setDayModalHasMore] = useState(false);
    const [isDayModalLoadingMore, setIsDayModalLoadingMore] = useState(false);
    const [dayModalError, setDayModalError] = useState<string | null>(null);
    const dayRequestGuardRef = useRef(createCalendarDayRequestGuard());
    const [currentViewType, setCurrentViewType] = useState<CalendarViewMode>(initialDefaultViewMode);
    const [visibleRangeStart, setVisibleRangeStart] = useState<Date | null>(null);
    const [visibleRangeEnd, setVisibleRangeEnd] = useState<Date | null>(null);
    /** Start of the interval the calendar view represents (from FullCalendar `view.currentStart`). */
    const [calendarViewPeriodStart, setCalendarViewPeriodStart] = useState<Date | null>(null);
    const [pendingYearAnchorDate, setPendingYearAnchorDate] = useState<Date | null>(null);
    const [isViewTransitionPending, startViewTransition] = useTransition();

    useEffect(() => {
        const guard = dayRequestGuardRef.current;
        guard.invalidate();
        setIsDayEventsModalOpen(false);
        setIsDayModalLoadingMore(false);
        setDayModalError(null);
        return () => {
            guard.invalidate();
        };
    }, [dayModalRequestContextKey]);

    const eventsWithColor = useMemo(() => {
        const withExtendedType = events.map((event) => ({
            ...event,
            extendedProps: {
                ...event.extendedProps,
                rawRow: event.extendedProps?.rawRow,
                type: event.type,
                typeLabel: event.type === undefined ? undefined : getEventTypeLabel?.(event.type),
                templateLabel: event.templateLabel ?? event.extendedProps?.templateLabel,
                templateStrokeColor: event.templateStrokeColor ?? event.extendedProps?.templateStrokeColor,
            },
        }));
        const withTypeColors =
            getEventColor === undefined
                ? withExtendedType
                : withExtendedType.map((event) => {
                      const color = getEventColor(event.type);
                      if (color.backgroundColor === undefined && color.borderColor === undefined && color.textColor === undefined) {
                          return event;
                      }
                      return {
                          ...event,
                          backgroundColor: color.backgroundColor,
                          borderColor: color.borderColor,
                          textColor: color.textColor,
                      };
                  });
        /* Match modal / schedule list: all-day tiles default to accent green + inverse text. */
        const allDayBackground = token("color.icon.accent.green");
        const allDayText = token("color.text.inverse");
        return withTypeColors.map((event) => {
            if (event.allDay !== true || allDayStyle.mode !== "fixedGreen") {
                return event;
            }
            return {
                ...event,
                backgroundColor: allDayBackground,
                borderColor: allDayBackground,
                textColor: allDayText,
            };
        });
    }, [allDayStyle.mode, events, getEventColor, getEventTypeLabel]);
    const deferredEventsWithColor = useDeferredValue(eventsWithColor);
    const isTimeGridView = currentViewType.startsWith("timeGrid") === true;
    const useEventCountDisplay = eventOpensDaySummaryModal === true || isTimeGridView === true;
    const calendarDisplayEvents = useMemo(() => {
        if (isTimeGridView === true) {
            return collapseTimedEventsToCountSummaries(deferredEventsWithColor);
        }
        return deferredEventsWithColor;
    }, [currentViewType, deferredEventsWithColor, isTimeGridView]);
    const computedLegendItems = useMemo(() => {
        if (legendItems !== undefined && legendItems.length > 0) {
            return legendItems.map((item) => ({
                type: item.type,
                color: getEventColor?.(item.type),
                label: item.label,
                description: item.description,
            }));
        }
        const eventTypes = Array.from(new Set(deferredEventsWithColor.map((event) => event.type).filter((type): type is string => type !== undefined)));
        return eventTypes.map((type) => ({
            type,
            color: getEventColor?.(type),
            label: getEventTypeLabel?.(type) ?? type,
        }));
    }, [deferredEventsWithColor, getEventColor, getEventTypeLabel, legendItems]);
    const shouldComputeYearViewStats = externalYearStats === undefined && currentViewType === "multiMonthYear";
    const {stats, isComputing} = useCalendarStats({
        eventsWithColor: deferredEventsWithColor,
        shouldComputeYearViewStats: shouldComputeYearViewStats,
        getEventColor: getEventColor,
        getEventTypeLabel: getEventTypeLabel,
    });
    const {eventDayKeys, monthEventTotals, monthTypeBars, yearViewDayStats} = externalYearStats ?? stats;
    const isCalendarUpdating =
        deferredEventsWithColor !== eventsWithColor ||
        isViewTransitionPending === true ||
        (externalYearStats === undefined && isComputing === true);

    const onEventSelected = useCallback(
        (event: CalendarSelectableEvent) => {
            const extendedProps = event.extendedProps as
                | {rawRow?: T; type?: string; typeLabel?: string; templateLabel?: string; templateStrokeColor?: string}
                | undefined;
            const rawRow = extendedProps?.rawRow;
            const beatEvent: BeatPlanningCalendarEvent = {
                id: event.id,
                title: event.title,
                start: event.start ?? new Date(),
                end: event.end ?? new Date(),
                allDay: event.allDay,
                type: extendedProps?.type,
                typeLabel: extendedProps?.typeLabel,
                templateLabel: extendedProps?.templateLabel,
                templateStrokeColor: extendedProps?.templateStrokeColor,
                extendedProps: {
                    rawRow,
                    typeLabel: extendedProps?.typeLabel,
                    templateLabel: extendedProps?.templateLabel,
                    templateStrokeColor: extendedProps?.templateStrokeColor,
                },
            };
            onEventClick(beatEvent, rawRow);
        },
        [onEventClick],
    );

    const beatEventToSelectable = useCallback((event: BeatPlanningCalendarEvent): CalendarSelectableEvent => {
        const start = new Date(event.start);
        const end = new Date(event.end);
        return {
            id: event.id,
            title: event.title,
            start: Number.isNaN(start.getTime()) ? null : start,
            end: Number.isNaN(end.getTime()) ? null : end,
            allDay: event.allDay === true,
            extendedProps: event.extendedProps,
            backgroundColor: (event as BeatPlanningCalendarEvent & {backgroundColor?: string}).backgroundColor,
            borderColor: (event as BeatPlanningCalendarEvent & {borderColor?: string}).borderColor,
            textColor: (event as BeatPlanningCalendarEvent & {textColor?: string}).textColor,
        };
    }, []);

    const getDayEvents = useCallback(
        (date: Date, calendarInstanceRef: RefObject<FullCalendar | null>): Array<CalendarSelectableEvent> => {
            const dayStart = new Date(date);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(dayStart);
            dayEnd.setDate(dayEnd.getDate() + 1);

            if (useEventCountDisplay === true) {
                return deferredEventsWithColor
                    .filter((event) => {
                        const start = new Date(event.start);
                        if (Number.isNaN(start.getTime())) {
                            return false;
                        }
                        const end = new Date(event.end);
                        const safeEnd = Number.isNaN(end.getTime()) || end.getTime() <= start.getTime() ? new Date(start.getTime() + 1) : end;
                        return start.getTime() < dayEnd.getTime() && safeEnd.getTime() > dayStart.getTime();
                    })
                    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
                    .map((event) => beatEventToSelectable(event));
            }

        const api = calendarInstanceRef.current?.getApi();
        if (api === undefined) {
            return [];
        }
        return api
            .getEvents()
            .filter((event) => {
                const eventStart = event.start;
                if (eventStart === null) {
                    return false;
                }
                const eventEnd = event.end ?? new Date(eventStart.getTime() + 1);
                return eventStart.getTime() < dayEnd.getTime() && eventEnd.getTime() > dayStart.getTime();
            })
            .sort((a, b) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0))
            .map((event) => ({
                id: event.id,
                title: event.title,
                start: event.start,
                end: event.end,
                allDay: event.allDay,
                extendedProps: event.extendedProps,
                backgroundColor: event.backgroundColor,
                borderColor: event.borderColor,
                textColor: event.textColor,
            }));
    }, [beatEventToSelectable, deferredEventsWithColor, useEventCountDisplay]);

    const fetchInitialDayEvents = useCallback((date: Date) => {
        if (fetchDayEventsForModal === undefined) return;
        const guard = dayRequestGuardRef.current;
        const token = guard.startInitial();
        setSelectedPeriodEvents([]);
        setDayModalHasMore(false);
        setDayModalError(null);
        setIsDayEventsModalOpen(true);
        setIsDayModalLoadingMore(true);
        void fetchDayEventsForModal(date, 0)
            .then((periodEvents) => {
                if (guard.isCurrent(token) === false) return;
                setSelectedPeriodEvents(periodEvents);
                setDayModalHasMore(periodEvents.length >= CALENDAR_DAY_MODAL_PAGE_SIZE);
            })
            .catch(() => {
                if (guard.isCurrent(token) === false) return;
                setDayModalError("Unable to load events for this day.");
            })
            .finally(() => {
                if (guard.isCurrent(token) === false) return;
                guard.finish(token);
                setIsDayModalLoadingMore(false);
            });
    }, [fetchDayEventsForModal]);

    const openDayEventsModal = useCallback((
        date: Date,
        calendarInstanceRef: RefObject<FullCalendar | null>,
        periodEventsOverride?: Array<CalendarSelectableEvent>,
    ) => {
        const periodTitle = DateTime.fromJSDate(date, {zone: "Asia/Kolkata"}).toFormat("cccc, d LLL yyyy");
        setSelectedPeriodTitle(periodTitle);
        setSelectedDayDate(date);

        if (fetchDayEventsForModal !== undefined) {
            fetchInitialDayEvents(date);
            return;
        }

        const periodEvents =
            periodEventsOverride ??
            (() => {
                const dayKey = toLocalDayKey(date);
                if (eventDayKeys.has(dayKey) === false) {
                    return [];
                }
                return getDayEvents(date, calendarInstanceRef);
            })();
        if (periodEvents.length === 0) {
            return;
        }
        setSelectedPeriodEvents(periodEvents);
        setDayModalHasMore(false);
        setIsDayEventsModalOpen(true);
    }, [eventDayKeys, fetchDayEventsForModal, fetchInitialDayEvents, getDayEvents]);

    const loadMoreDayEvents = useCallback(() => {
        if (fetchDayEventsForModal === undefined || selectedDayDate === null) {
            return;
        }
        const guard = dayRequestGuardRef.current;
        const token = guard.startLoadMore();
        if (token === null) return;
        const offset = selectedPeriodEvents.length;
        setDayModalError(null);
        setIsDayModalLoadingMore(true);
        void fetchDayEventsForModal(selectedDayDate, offset)
            .then((nextPage) => {
                if (guard.isCurrent(token) === false) return;
                setSelectedPeriodEvents((previous) => [...previous, ...nextPage]);
                setDayModalHasMore(nextPage.length >= CALENDAR_DAY_MODAL_PAGE_SIZE);
            })
            .catch(() => {
                if (guard.isCurrent(token) === false) return;
                setDayModalError("Unable to load more events.");
            })
            .finally(() => {
                if (guard.isCurrent(token) === false) return;
                guard.finish(token);
                setIsDayModalLoadingMore(false);
            });
    }, [fetchDayEventsForModal, selectedDayDate, selectedPeriodEvents.length]);

    const closeDayEventsModal = useCallback(() => {
        dayRequestGuardRef.current.invalidate();
        setIsDayEventsModalOpen(false);
        setIsDayModalLoadingMore(false);
        setDayModalError(null);
    }, []);

    const retryDayEvents = useCallback(() => {
        if (selectedDayDate === null) return;
        if (selectedPeriodEvents.length === 0) {
            fetchInitialDayEvents(selectedDayDate);
            return;
        }
        loadMoreDayEvents();
    }, [fetchInitialDayEvents, loadMoreDayEvents, selectedDayDate, selectedPeriodEvents.length]);

    const getApi = useCallback((view: CalendarViewMode = currentViewType): CalendarApi | undefined => {
        if (view === "multiMonthYear") {
            return yearCalendarRef.current?.getApi();
        }
        return nonYearCalendarRef.current?.getApi();
    }, [currentViewType]);

    const scheduleCalendarSizeUpdate = useCallback(() => {
        if (currentViewType === "scheduleList") {
            return;
        }
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                getApi()?.updateSize();
            });
        });
    }, [currentViewType, getApi]);

    const {theme} = useAnankePreferences();

    const onYearDatesSet = useCallback((args: DatesSetArg) => {
        if (currentViewType !== "multiMonthYear") {
            return;
        }
        setVisibleRangeStart(args.start);
        setVisibleRangeEnd(args.end);
        setCalendarViewPeriodStart(args.view.currentStart);
        onVisibleRangeChange?.({
            start: args.start,
            end: args.end,
        });
        setCalendarToolbarState({title: args.view.title});
        queueMicrotask(() => {
            const api = yearCalendarRef.current?.getApi();
            const root = calendarRootRef.current;
            if (api !== undefined && root !== null) {
                syncMultiMonthYearHeaderTotals(root, api, monthEventTotals, monthTypeBars);
            }
        });
    }, [currentViewType, onVisibleRangeChange, monthEventTotals, monthTypeBars]);

    const onNonYearDatesSet = useCallback((args: DatesSetArg) => {
        if (currentViewType === "multiMonthYear") {
            return;
        }
        setVisibleRangeStart(args.start);
        setVisibleRangeEnd(args.end);
        setCalendarViewPeriodStart(args.view.currentStart);
        onVisibleRangeChange?.({
            start: args.start,
            end: args.end,
        });
        setCalendarToolbarState({title: args.view.title});
    }, [currentViewType, onVisibleRangeChange]);

    const onYearEventsSet = useCallback(() => {
        if (currentViewType !== "multiMonthYear") {
            return;
        }
        queueMicrotask(() => {
            const api = yearCalendarRef.current?.getApi();
            const root = calendarRootRef.current;
            if (api === undefined || root === null) {
                return;
            }
            if (api.view.type !== "multiMonthYear") {
                return;
            }
            syncMultiMonthYearHeaderTotals(root, api, monthEventTotals, monthTypeBars);
        });
    }, [currentViewType, monthEventTotals, monthTypeBars]);

    const moveCalendar = useCallback((direction: "prev" | "next") => {
        const api = getApi();
        if (api === undefined) {
            return;
        }
        if (direction === "prev") {
            api.prev();
            return;
        }
        api.next();
    }, [getApi]);

    const calendarSwipeNavigation = useCalendarSwipeNavigation({
        enabled: isMobile === true,
        onPrev: () => {
            moveCalendar("prev");
        },
        onNext: () => {
            moveCalendar("next");
        },
    });

    const jumpToToday = useCallback(() => {
        const api = getApi();
        if (api === undefined) {
            return;
        }
        api.today();
    }, [getApi]);

    const switchView = useCallback((view: CalendarViewMode) => {
        if (view === currentViewType && view !== "scheduleList") {
            return;
        }
        const sourceApi = getApi(currentViewType);
        const sourceDate = sourceApi?.getDate();
        const nonYearApi = nonYearCalendarRef.current?.getApi();
        if (view === "multiMonthYear") {
            setPendingYearAnchorDate(sourceDate ?? null);
            startViewTransition(() => {
                setCurrentViewType(view);
                onCalendarViewModeChange?.(view);
                setCalendarToolbarState({currentViewMode: view});
            });
            return;
        }
        startViewTransition(() => {
            setCurrentViewType(view);
            onCalendarViewModeChange?.(view);
            setCalendarToolbarState({currentViewMode: view});
        });
        if (view === "scheduleList") {
            if (sourceDate !== undefined) {
                nonYearApi?.gotoDate(sourceDate);
            }
            nonYearApi?.changeView("dayGridMonth");
            return;
        }
        if (sourceDate !== undefined) {
            nonYearApi?.gotoDate(sourceDate);
        }
        nonYearApi?.changeView(view);
    }, [currentViewType, getApi, onCalendarViewModeChange, startViewTransition]);

    const switchViewRef = useRef(switchView);
    switchViewRef.current = switchView;

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const mediaQueryList = window.matchMedia(MOBILE_CALENDAR_VIEWPORT_MEDIA_QUERY);

        const applyViewportDefaultView = () => {
            const expectedMode = getDefaultCalendarViewModeForViewport(mediaQueryList.matches);
            switchViewRef.current(expectedMode);
        };

        applyViewportDefaultView();
        mediaQueryList.addEventListener("change", applyViewportDefaultView);
        return () => {
            mediaQueryList.removeEventListener("change", applyViewportDefaultView);
        };
    }, []);

    useEffect(() => {
        if (currentViewType !== "multiMonthYear") {
            return;
        }
        if (pendingYearAnchorDate === null) {
            return;
        }
        const yearApi = yearCalendarRef.current?.getApi();
        if (yearApi === undefined) {
            return;
        }
        yearApi.gotoDate(pendingYearAnchorDate);
        setPendingYearAnchorDate(null);
    }, [currentViewType, pendingYearAnchorDate]);

    useEffect(() => {
        onCalendarViewModeChange?.(currentViewType);
    }, [currentViewType, onCalendarViewModeChange]);
    useEffect(() => {
        setCalendarToolbarState({
            handlers: {
                prev: () => moveCalendar("prev"),
                next: () => moveCalendar("next"),
                today: jumpToToday,
                setView: switchView,
            },
            legendItems: computedLegendItems,
            currentViewMode: currentViewType,
        });
        return () => {
            clearCalendarToolbarHandlers();
        };
    }, [computedLegendItems, currentViewType, moveCalendar, jumpToToday, switchView]);
    useEffect(() => {
        scheduleCalendarSizeUpdate();
    }, [currentViewType, scheduleCalendarSizeUpdate]);

    useEffect(() => {
        scheduleCalendarSizeUpdate();
    }, [asidePanelContext?.state.isFullscreen, asidePanelContext?.state.isOpen, asidePanelContext?.state.width, scheduleCalendarSizeUpdate]);

    useEffect(() => {
        if (currentViewType === "scheduleList") {
            return;
        }
        const container = calendarContainerRef.current;
        if (container === null) {
            return;
        }
        const resizeObserver = new ResizeObserver(() => {
            scheduleCalendarSizeUpdate();
        });
        resizeObserver.observe(container);
        return () => {
            resizeObserver.disconnect();
        };
    }, [currentViewType, scheduleCalendarSizeUpdate]);

    useEffect(() => {
        if (currentViewType !== "multiMonthYear") {
            return;
        }
        queueMicrotask(() => {
            const api = yearCalendarRef.current?.getApi();
            const root = calendarRootRef.current;
            if (api === undefined || root === null) {
                return;
            }
            syncMultiMonthYearHeaderTotals(root, api, monthEventTotals, monthTypeBars);
        });
    }, [currentViewType, monthEventTotals, monthTypeBars]);

    const scheduleGroups = useMemo(() => {
        if (currentViewType !== "scheduleList" || visibleRangeStart === null || visibleRangeEnd === null) {
            return [];
        }
        const groups: Array<CalendarScheduleGroup> = [];
        const cursor = new Date(visibleRangeStart);
        cursor.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(visibleRangeEnd);
        rangeEnd.setHours(0, 0, 0, 0);
        while (cursor.getTime() < rangeEnd.getTime()) {
            const dayStart = new Date(cursor);
            const dayEnd = new Date(cursor);
            dayEnd.setDate(dayEnd.getDate() + 1);
            const dayEvents = deferredEventsWithColor
                .map((event) => {
                    const start = new Date(event.start);
                    const end = new Date(event.end);
                    const safeEnd =
                        Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()
                            ? new Date(start.getTime() + 1)
                            : end;
                    return {
                        event,
                        start,
                        end: safeEnd,
                    };
                })
                .filter((item) => {
                    if (Number.isNaN(item.start.getTime())) {
                        return false;
                    }
                    return item.start.getTime() < dayEnd.getTime() && item.end.getTime() > dayStart.getTime();
                })
                .sort((a, b) => a.start.getTime() - b.start.getTime())
                .map((item) => ({
                    key: `${item.event.id}-${item.start.toISOString()}`,
                    event: item.event,
                    backgroundColor: (item.event as BeatPlanningCalendarEvent & {backgroundColor?: string}).backgroundColor,
                    borderColor: (item.event as BeatPlanningCalendarEvent & {borderColor?: string}).borderColor,
                    textColor: (item.event as BeatPlanningCalendarEvent & {textColor?: string}).textColor,
                    start: item.start,
                    end: item.end,
                }));
            if (dayEvents.length > 0) {
                const dayMetaDt = DateTime.fromJSDate(dayStart, {zone: "Asia/Kolkata"});
                groups.push({
                    dayKey: toLocalDayKey(dayStart),
                    dayNumber: String(dayStart.getDate()),
                    dayMeta: `${dayMetaDt.toFormat("LLL").toUpperCase()}, ${dayMetaDt.toFormat("ccc").toUpperCase()}`,
                    events: dayEvents,
                });
            }
            cursor.setDate(cursor.getDate() + 1);
        }
        return groups;
    }, [currentViewType, deferredEventsWithColor, visibleRangeStart, visibleRangeEnd]);

    const renderEmptyState = useCallback((): ReactNode => {
        const emptyStateCopy = scheduleEmptyState ?? {
            createButtonLabel: "Create event",
            title: "No events scheduled yet",
            titleWithFilters: "No events match your filters",
            description: "Nothing is scheduled for this period yet. Create your first item to see it on the calendar.",
            descriptionWithFilters: "Try adjusting your filters to see more items, or create a new one.",
        };
        const currentCalendarYear = new Date().getFullYear();
        const viewYear = calendarViewPeriodStart?.getFullYear();
        const isCreateDisabled =
            disableCreateEventButton === true ||
            (disableCreateEventWhenViewYearAfterCurrent === true &&
                viewYear !== undefined &&
                viewYear > currentCalendarYear);
        return (
            <div className="beat-planning-calendar__schedule-empty-state">
                <span className="beat-planning-calendar__schedule-empty-state-icon">
                    <InformationCircleIcon
                        label=""
                        color={token("color.icon.disabled")}
                    />
                </span>
                <div className="beat-planning-calendar__schedule-empty-state-content">
                    <div className="beat-planning-calendar__schedule-empty-state-title">
                        {hasActiveFilters === true ? emptyStateCopy.titleWithFilters : emptyStateCopy.title}
                    </div>
                    <div className="beat-planning-calendar__schedule-empty-state-description">
                        {hasActiveFilters === true
                            ? emptyStateCopy.descriptionWithFilters
                            : emptyStateCopy.description}
                    </div>
                </div>
                {onCreateEventClick !== undefined ? (
                    <AnankeButton
                        type="button"
                        appearance="primary"
                        iconBefore={(iconProps) => (
                            <AddIcon
                                {...iconProps}
                                label=""
                            />
                        )}
                        isDisabled={isCreateDisabled}
                        onClick={isCreateDisabled === true ? undefined : onCreateEventClick}
                    >
                        {emptyStateCopy.createButtonLabel}
                    </AnankeButton>
                ) : null}
            </div>
        );
    }, [
        calendarViewPeriodStart,
        disableCreateEventButton,
        disableCreateEventWhenViewYearAfterCurrent,
        hasActiveFilters,
        onCreateEventClick,
        scheduleEmptyState,
    ]);

    const calendarRootClassName = [
        "beat-planning-calendar",
        useEventCountDisplay === true ? "beat-planning-calendar--event-count-display" : "",
        className,
    ]
        .filter((value) => value !== "")
        .join(" ");

    return (
        <div
            ref={calendarContainerRef}
            className={calendarRootClassName}
            data-theme={theme}
            data-mobile-swipe-nav={isMobile === true ? "true" : undefined}
            onTouchStart={calendarSwipeNavigation.onTouchStart}
            onTouchEnd={calendarSwipeNavigation.onTouchEnd}
            onTouchCancel={calendarSwipeNavigation.onTouchCancel}
        >
            {currentViewType === "multiMonthYear" ? (
                <CalendarYearView
                    isMobile={isMobile}
                    yearCalendarRef={yearCalendarRef}
                    calendarRootRef={calendarRootRef}
                    events={deferredEventsWithColor}
                    eventDayKeys={eventDayKeys}
                    yearViewDayStats={yearViewDayStats}
                    onYearDatesSet={onYearDatesSet}
                    onYearEventsSet={onYearEventsSet}
                    openDayEventsModal={openDayEventsModal}
                    onEventSelected={onEventSelected}
                    eventOpensDaySummaryModal={eventOpensDaySummaryModal}
                />
            ) : null}
            <div
                style={
                    currentViewType === "multiMonthYear"
                        ? {display: "none"}
                        : currentViewType === "scheduleList"
                          ? {
                                /* FullCalendar stays mounted for range + API, but must not reserve flex height */
                                height: 0,
                                minHeight: 0,
                                flex: "0 0 auto",
                                overflow: "hidden",
                            }
                          : {height: "100%"}
                }
            >
                <CalendarMonthWeekDayView
                    nonYearCalendarRef={nonYearCalendarRef}
                    initialNonYearCalendarViewMode={initialNonYearCalendarViewMode}
                    currentViewType={currentViewType}
                    isMobile={isMobile}
                    events={calendarDisplayEvents}
                    eventDayKeys={eventDayKeys}
                    onNonYearDatesSet={onNonYearDatesSet}
                    onEventSelected={onEventSelected}
                    openDayEventsModal={openDayEventsModal}
                    beatEventToSelectable={beatEventToSelectable}
                    eventOpensDaySummaryModal={eventOpensDaySummaryModal}
                    useEventCountDisplay={useEventCountDisplay}
                    use24HourTimeAxis={use24HourTimeAxis}
                />
            </div>
            {currentViewType === "scheduleList" ? (
                <CalendarScheduleList
                    scheduleGroups={scheduleGroups}
                    allDayStyle={allDayStyle}
                    startTimeOnly={startTimeOnly}
                    getEventColor={getEventColor}
                    renderEmptyState={renderEmptyState}
                    isScheduleDataLoading={isScheduleDataLoading}
                    hasMore={scheduleHasMore}
                    isLoadingMore={isScheduleLoadingMore}
                    loadMoreError={scheduleLoadMoreError}
                    onLoadMore={onScheduleLoadMore}
                    onEventSelected={onEventSelected}
                    eventOpensDaySummaryModal={eventOpensDaySummaryModal}
                    onOpenDaySummaryForEventStart={
                        eventOpensDaySummaryModal === true
                            ? (start) => {
                                  openDayEventsModal(start, nonYearCalendarRef);
                              }
                            : undefined
                    }
                />
            ) : null}
            <CalendarDayEventsModal
                opened={isDayEventsModalOpen}
                title={selectedPeriodTitle}
                selectedPeriodEvents={selectedPeriodEvents}
                onClose={closeDayEventsModal}
                onEventSelected={onEventSelected}
                getEventColor={getEventColor}
                allDayStyle={allDayStyle}
                hasMore={dayModalHasMore}
                isLoadingMore={isDayModalLoadingMore}
                error={dayModalError}
                onRetry={retryDayEvents}
                onLoadMore={fetchDayEventsForModal === undefined ? undefined : loadMoreDayEvents}
                presentation={
                    daySummaryModalPresentation === undefined
                        ? undefined
                        : {
                              ...daySummaryModalPresentation,
                              getSecondaryLine: (row: unknown) =>
                                  daySummaryModalPresentation.getSecondaryLine(row as T),
                          }
                }
            />
            {isCalendarUpdating === true ? (
                <div className="beat-planning-calendar__updating-overlay">
                    <div className="beat-planning-calendar__updating-pill">Updating calendar...</div>
                </div>
            ) : null}
        </div>
    );
}
